import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { useToastContext } from '../../../../../contexts/toast'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../utils/helper/validateFormikFields'
import { DefaultConfig } from '../../../../ui-config/data'
import { DataForArticleDto, DataForArticlePutDto } from '../../../data/model'
import { ArticleDetailFormView } from './ArticleDetailForm.view'

type Props = {
  data: DataForArticleDto
  config: DefaultConfig
  onSubmit: (formData: DataForArticlePutDto) => void
  isLoading: boolean
  hasBatches: boolean
  children?: JSX.Element
}

interface FormValues extends DataForArticleDto {}
export type FormFieldName = keyof DataForArticleDto

export const ArticleDetailFormForm: React.FC<Props> = ({
  data,
  config,
  onSubmit,
  isLoading,
  hasBatches,
  children,
}) => {
  const { t } = useTranslation()

  const { showToast } = useToastContext()

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { ...data },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationQuantity',
          config:
            config.deliveryForecastDespatchAdvice.articleDetail
              .confirmationQuantity,
        },
        {
          fieldName: 'deliveryStatus',
          config:
            config.deliveryForecastDespatchAdvice.articleDetail.deliveryStatus,
        },
        {
          fieldName: 'confirmationNetWeight',
          config:
            config.deliveryForecastDespatchAdvice.articleDetail
              .confirmationNetWeight,
        },
        {
          fieldName: 'confirmationNetWeightUnit',
          config:
            config.deliveryForecastDespatchAdvice.articleDetail
              .confirmationNetWeightUnit,
        },
        {
          fieldName: 'confirmationCountryOfOrigin',
          config:
            config.deliveryForecastDespatchAdvice.articleDetail
              .confirmationCountryOfOrigin,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      if (!data.confirmationQuantity && data.confirmationQuantity !== 0) {
        errors.confirmationQuantity = t(
          'deliveryForecastDespatchAdvice.articleDetail.form.confirmationQuantity.validation.required',
        )
      } else if (data.confirmationQuantity < 1) {
        errors.confirmationQuantity = t(
          'deliveryForecastDespatchAdvice.articleDetail.form.confirmationQuantity.validation.numberTooSmall',
          { number: 0 },
        )
      }

      // check deliveryStatus
      if (getOpenQuantity() > 0) {
        if (!data.deliveryStatus || data.deliveryStatus === 'NOT_SET') {
          errors.deliveryStatus = t(
            'deliveryForecastDespatchAdvice.articleDetail.form.deliveryStatus.validation.required',
          )
        }
      }

      return errors
    },
    onSubmit: data => {
      try {
        // send data in DespatchAdviceData
        onSubmit(data)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'deliveryForecastDespatchAdvice.articleDetail.save.messages.success.summary',
          ),
          detail: t(
            'deliveryForecastDespatchAdvice.articleDetail.save.messages.success.detail',
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'deliveryForecastDespatchAdvice.articleDetail.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  const getOpenQuantity = () => {
    const openQuantity = formik.values.openQuantity || formik.values.quantity
    const confirmedQuantity = formik.values.confirmationQuantity

    return openQuantity - confirmedQuantity
  }

  return (
    <ArticleDetailFormView
      config={config}
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
      isLoading={isLoading}
      hasBatches={hasBatches}
      openQuantity={getOpenQuantity()}
    >
      {children}
    </ArticleDetailFormView>
  )
}
